export { shouldAutoEnableSelectedRoofPlacement } from '@aerialytic/designer-states/core';

export const createPanelPlacementActionId = () => {
  const randomUUID = globalThis?.crypto?.randomUUID;
  if (typeof randomUUID === 'function') {
    return `fill-panels-${randomUUID.call(globalThis.crypto)}`;
  }

  const randomPart = Math.random().toString(36).slice(2, 14);
  return `fill-panels-${Date.now().toString(36)}-${randomPart}`;
};

export const countPlacedPanels = (panels, segmentIndices = null) => {
  const scopedPanels = Array.isArray(segmentIndices)
    ? [...new Set(segmentIndices)]
        .map((index) => panels?.[index])
        .filter(Boolean)
    : panels;

  return Array.isArray(scopedPanels)
    ? scopedPanels.reduce(
        (count, panel) =>
          count +
          (Array.isArray(panel?.horizontal?.points)
            ? panel.horizontal.points.length
            : 0) +
          (Array.isArray(panel?.vertical?.points)
            ? panel.vertical.points.length
            : 0),
        0
      )
    : 0;
};

export const countAddedPanels = (
  panels,
  baselinePanelCount = 0,
  segmentIndices = null
) =>
  Math.max(
    0,
    countPlacedPanels(panels, segmentIndices) -
      Math.max(0, Number(baselinePanelCount) || 0)
  );

const panelGeometry = (panels) =>
  (Array.isArray(panels) ? panels : []).map((panel) => ({
    id: panel?.id ?? null,
    horizontal: Array.isArray(panel?.horizontal?.points)
      ? panel.horizontal.points
      : [],
    vertical: Array.isArray(panel?.vertical?.points)
      ? panel.vertical.points
      : []
  }));

export const panelGeometryFingerprint = (panels) => {
  const serialized = JSON.stringify(panelGeometry(panels));
  let hash = 2166136261;
  for (let index = 0; index < serialized.length; index += 1) {
    hash ^= serialized.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
};

export const buildPanelPlacementOutcome = ({
  actionId,
  panels,
  persistedPanels,
  placementPanelCount = countPlacedPanels(panels),
  baselinePanelCount = 0
}) => {
  if (!Array.isArray(persistedPanels)) {
    throw new Error('Canvas persistence response did not include panels');
  }

  const panelCount = countPlacedPanels(panels);
  const persistedPanelCount = countPlacedPanels(persistedPanels);
  const generatedGeometry = JSON.stringify(panelGeometry(panels));
  const persistedGeometry = JSON.stringify(panelGeometry(persistedPanels));
  if (
    persistedPanelCount !== panelCount ||
    persistedGeometry !== generatedGeometry
  ) {
    throw new Error(
      `Canvas persistence mismatch: generated ${panelCount}, saved ${persistedPanelCount}`
    );
  }

  const status =
    placementPanelCount !== 0
      ? 'success'
      : Math.max(0, Number(baselinePanelCount) || 0) > 0
        ? 'unchanged'
        : 'empty';

  return {
    actionId,
    status,
    placementPanelCount,
    panelCount,
    persistedPanelCount,
    geometryFingerprint: panelGeometryFingerprint(panels)
  };
};

export const persistPanelPlacement = async ({
  actionId,
  panels,
  placementPanelCount,
  baselinePanelCount,
  save
}) => {
  if (typeof save !== 'function') {
    throw new Error('Canvas persistence is unavailable');
  }
  const persistedCanvas = await save();
  return buildPanelPlacementOutcome({
    actionId,
    panels,
    persistedPanels: persistedCanvas?.panels,
    placementPanelCount,
    baselinePanelCount
  });
};

export const panelPlacementOutcomeMessage = ({
  actionId,
  status,
  placementPanelCount
}) => {
  if (status === 'empty') {
    return `No panels fit the selected roof area. Reference: ${actionId}`;
  }
  if (status === 'unchanged') {
    return `No new panels were added. Reference: ${actionId}`;
  }
  return `${placementPanelCount} panel${placementPanelCount === 1 ? '' : 's'} filled and saved. Reference: ${actionId}`;
};
